import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import {
  canManageAutomationsForRole,
  defaultConditionFor,
  isConditionValid,
  newConditionId,
  toApiCondition,
  EMPTY_FORM,
  MAX_CONDITIONS,
  type ConditionField,
  type ConditionFormState,
  type ConditionOperator,
  type RuleFormState,
} from '@/lib/automations';
import type {
  AutomationRule,
  AutomationRun,
  CreateAutomationRuleRequest,
  UpdateAutomationRuleRequest,
} from '@/api/generated/client';

const AUTOMATIONS_QUERY_KEY = ['automations'];

export function useAutomations() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManageAutomations = canManageAutomationsForRole(user?.role);

  const { data, isLoading, error } = useQuery({
    queryKey: AUTOMATIONS_QUERY_KEY,
    queryFn: () => apiClient.automations.listAutomationRules(),
    enabled: canManageAutomations,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: AUTOMATIONS_QUERY_KEY });

  const createMutation = useMutation({
    mutationFn: (request: CreateAutomationRuleRequest) =>
      apiClient.automations.createAutomationRule({
        createAutomationRuleRequest: request,
      }),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: ({
      ruleId,
      request,
    }: {
      ruleId: number;
      request: UpdateAutomationRuleRequest;
    }) =>
      apiClient.automations.updateAutomationRule({
        ruleId,
        updateAutomationRuleRequest: request,
      }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (ruleId: number) =>
      apiClient.automations.deleteAutomationRule({ ruleId }),
    onSuccess: invalidate,
  });

  const rules: AutomationRule[] = data?.rules ?? [];

  return {
    rules,
    isLoading,
    error,
    canManageAutomations,
    createRule: createMutation,
    updateRule: updateMutation,
    deleteRule: deleteMutation,
  };
}

export function useAutomationRuns(ruleId: number | null) {
  const { user } = useAuth();
  const canManageAutomations = canManageAutomationsForRole(user?.role);

  const { data, isLoading, error } = useQuery({
    queryKey: ['automations', ruleId, 'runs'],
    queryFn: () =>
      apiClient.automations.listAutomationRuleRuns({ ruleId: ruleId! }),
    enabled: canManageAutomations && ruleId !== null,
  });

  const runs: AutomationRun[] = data?.runs ?? [];

  return { runs, isLoading, error };
}

export function useAutomationsPage() {
  const { toast } = useToast();
  const {
    rules,
    isLoading,
    error,
    canManageAutomations,
    createRule,
    updateRule,
    deleteRule,
  } = useAutomations();

  const { data: templatesResponse } = useQuery({
    queryKey: ['form-templates', false],
    queryFn: async () => {
      const response = await apiClient.formTemplates.listFormTemplates({ includeInactive: false });
      return response.templates;
    },
    enabled: canManageAutomations,
  });

  const templates = useMemo(() => templatesResponse ?? [], [templatesResponse]);
  const templateNameById = useMemo(
    () => new Map(templates.map((template) => [template.id, template.name])),
    [templates],
  );

  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);
  const [form, setForm] = useState<RuleFormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<AutomationRule | null>(null);
  const [runsRule, setRunsRule] = useState<AutomationRule | null>(null);

  const openCreate = () => {
    setEditingRule(null);
    setForm(EMPTY_FORM);
    setIsBuilderOpen(true);
  };

  const openEdit = (rule: AutomationRule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      conditions: (rule.conditions ?? []).map((condition) => ({
        id: newConditionId(),
        field: condition.field as ConditionField,
        operator: condition.operator as ConditionOperator,
        value: String(condition.value),
      })),
      formTemplateId: String(rule.actionConfig.formTemplateId),
      isActive: rule.isActive,
    });
    setIsBuilderOpen(true);
  };

  const closeBuilder = () => {
    setIsBuilderOpen(false);
    setEditingRule(null);
  };

  const updateCondition = (conditionId: string, patch: Partial<ConditionFormState>) => {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.map((condition) =>
        condition.id === conditionId ? { ...condition, ...patch } : condition,
      ),
    }));
  };

  // Switching the field resets operator/value to that field's valid defaults.
  const changeConditionField = (conditionId: string, field: ConditionField) => {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.map((condition) =>
        condition.id === conditionId ? { ...defaultConditionFor(field), id: condition.id } : condition,
      ),
    }));
  };

  const addCondition = () => {
    setForm((prev) =>
      prev.conditions.length >= MAX_CONDITIONS
        ? prev
        : { ...prev, conditions: [...prev.conditions, defaultConditionFor('source')] },
    );
  };

  const removeCondition = (conditionId: string) => {
    setForm((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((condition) => condition.id !== conditionId),
    }));
  };

  const isSaving = createRule.isPending || updateRule.isPending;
  const canSave =
    form.name.trim().length > 0 &&
    form.formTemplateId !== '' &&
    form.conditions.every(isConditionValid) &&
    !isSaving;

  const handleSave = async () => {
    const conditions = form.conditions.map(toApiCondition);
    const actionConfig = { formTemplateId: Number(form.formTemplateId) };

    try {
      if (editingRule) {
        await updateRule.mutateAsync({
          ruleId: editingRule.id,
          request: {
            name: form.name.trim(),
            conditions,
            actionConfig,
            isActive: form.isActive,
          },
        });
        toast({ title: 'Automation updated' });
      } else {
        await createRule.mutateAsync({
          name: form.name.trim(),
          triggerType: 'claim_created',
          conditions,
          actionType: 'send_form',
          actionConfig,
          isActive: form.isActive,
        });
        toast({ title: 'Automation created' });
      }
      closeBuilder();
    } catch {
      toast({
        title: 'Unable to save automation',
        description: 'Check the form details and try again.',
        variant: 'destructive',
      });
    }
  };

  const handleToggle = async (rule: AutomationRule, isActive: boolean) => {
    try {
      await updateRule.mutateAsync({ ruleId: rule.id, request: { isActive } });
    } catch {
      toast({ title: 'Unable to update automation', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteRule.mutateAsync(deleteTarget.id);
      toast({ title: 'Automation deleted' });
    } catch {
      toast({ title: 'Unable to delete automation', variant: 'destructive' });
    } finally {
      setDeleteTarget(null);
    }
  };

  const {
    runs,
    isLoading: isRunsLoading,
    error: runsError,
  } = useAutomationRuns(runsRule?.id ?? null);

  return {
    rules,
    isLoading,
    error,
    canManageAutomations,
    templates,
    templateNameById,
    isBuilderOpen,
    editingRule,
    form,
    setForm,
    deleteTarget,
    setDeleteTarget,
    runsRule,
    setRunsRule,
    runs,
    isRunsLoading,
    runsError,
    isSaving,
    canSave,
    openCreate,
    openEdit,
    closeBuilder,
    updateCondition,
    changeConditionField,
    addCondition,
    removeCondition,
    handleSave,
    handleToggle,
    handleDelete,
  };
}
